import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { formatIdr, formatRelative, parseBackendJson } from "@/lib/format";
import { catalogLabelForSlug } from "@/lib/catalog-labels";
import { Plus, X, Loader2, Save, Archive, Upload, Building2 } from "lucide-react";
import { CsvImporter } from "@/components/import/CsvImporter";
import { EmptyState } from "@/components/ui/empty-state";
import { useToast } from "@/lib/toast";
import { PROPERTY_STATUSES, PROPERTY_TYPES, type PropertyStatus, type PropertyType } from "@ai-leasing-os/shared";
import { cn } from "@/lib/cn";

interface ListRow {
  id: string;
  name: string;
  area: string;
  type: PropertyType;
  bedrooms: number | null;
  bathrooms: number | null;
  monthly_price_idr: number | null;
  status: PropertyStatus;
  available_from: string | null;
  short_description: string | null;
  lifestyle_tags: string[] | null;
  is_active: number;
  updated_at: string;
}

export function PropertiesPage() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<PropertyStatus | null>(null);
  const [editingId, setEditingId] = useState<string | "new" | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["properties", statusFilter],
    queryFn: () => {
      const params = new URLSearchParams({ limit: "200" });
      if (statusFilter) params.set("status", statusFilter);
      return api.get<ListRow[]>(`/api/properties?${params.toString()}`);
    },
  });

  // Vertical-aware labels. The underlying API is /api/properties for all
  // templates; the dashboard just relabels per active template slug
  // (Treatments / Services / Restaurants / Menu & Catering / Properties).
  const { data: business } = useQuery({
    queryKey: ["settings", "business"],
    queryFn: () =>
      api.get<{ current_template_slug: string | null }>(
        "/api/settings/business",
      ),
    staleTime: 30_000,
  });
  const catalog = catalogLabelForSlug(business?.current_template_slug);

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">{catalog.plural}</h1>
          <p className="text-sm text-slate-500">{catalog.description}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setImportOpen(true)}>
            <Upload className="h-4 w-4" /> Import CSV
          </Button>
          <Button onClick={() => setEditingId("new")}>
            <Plus className="h-4 w-4" /> New {catalog.singular.toLowerCase()}
          </Button>
        </div>
      </header>

      <CsvImporter
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onDone={() => qc.invalidateQueries({ queryKey: ["properties"] })}
        endpoint="/api/properties/import"
        entityLabel="Properties"
        columns={[
          { key: "name", required: true, example: "Villa Mantra" },
          { key: "area", required: true, example: "Canggu" },
          { key: "type", example: "villa", help: "villa/apartment/house/room/land" },
          { key: "bedrooms", example: "3" },
          { key: "bathrooms", example: "2" },
          { key: "monthly_price_idr", example: "40000000" },
          { key: "yearly_price_idr", example: "420000000" },
          { key: "deposit_idr", example: "40000000" },
          { key: "status", example: "available", help: "available/pending/rented/..." },
          { key: "available_from", example: "2026-07-01", help: "YYYY-MM-DD" },
          { key: "min_stay_months", example: "6" },
          { key: "short_description", example: "3-bed villa with pool, Berawa" },
          { key: "address", example: "Jl. Pantai Berawa No.42" },
          { key: "google_maps_url", example: "https://goo.gl/maps/..." },
        ]}
        coerceRow={(raw) => {
          const out: Record<string, unknown> = { ...raw };
          for (const k of ["bedrooms", "bathrooms", "monthly_price_idr", "yearly_price_idr", "deposit_idr", "min_stay_months"]) {
            if (raw[k] !== undefined && raw[k] !== "") out[k] = Number(raw[k]);
          }
          return out;
        }}
      />

      <Card className="p-3 flex flex-wrap items-center gap-2">
        <button
          onClick={() => setStatusFilter(null)}
          className={cn("px-2 py-0.5 rounded text-[11px] font-medium",
            statusFilter === null ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200",
          )}
        >
          all
        </button>
        {PROPERTY_STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s === statusFilter ? null : s)}
            className={cn("px-2 py-0.5 rounded text-[11px] font-medium",
              statusFilter === s ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200",
            )}
          >
            {s}
          </button>
        ))}
        <span className="ml-auto text-xs text-slate-500">
          {rows.length} {catalog.plural.toLowerCase()}
        </span>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map((p) => (
          <Card key={p.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setEditingId(p.id)}>
            <div className="p-4 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="font-semibold text-slate-900">{p.name}</h3>
                  <p className="text-xs text-slate-500">{p.area} · {p.type}</p>
                </div>
                <PropertyStatusBadge status={p.status} />
              </div>
              <p className="text-xs text-slate-600 line-clamp-2 h-8">
                {p.short_description ?? "(no description)"}
              </p>
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-600">
                  {p.bedrooms != null ? `${p.bedrooms} ${catalog.capacityLabel}` : "-"}
                  {p.bathrooms != null && ` · ${p.bathrooms} BA`}
                </span>
                <span className="font-semibold tabular-nums">
                  {p.monthly_price_idr != null
                    ? `${formatIdr(p.monthly_price_idr)}${catalog.priceSuffix}`
                    : "-"}
                </span>
              </div>
              {(() => {
                // lifestyle_tags is JSON-typed in TiDB; the serverless driver
                // hands it back as a string. Parse before iterating so the
                // chips actually render (and so .slice doesn't throw).
                const tags = parseBackendJson<string[]>(p.lifestyle_tags) ?? [];
                if (tags.length === 0) return null;
                return (
                  <div className="flex flex-wrap gap-1 pt-1">
                    {tags.slice(0, 4).map((t) => (
                      <Badge key={t} variant="muted">{t}</Badge>
                    ))}
                  </div>
                );
              })()}
              <div className="text-[10px] text-slate-400 pt-1">
                Updated {formatRelative(p.updated_at)}
              </div>
            </div>
          </Card>
        ))}
        {rows.length === 0 && (
          <Card className="md:col-span-2 lg:col-span-3">
            {isLoading ? (
              <div className="p-12 text-center text-sm text-slate-400">Loading…</div>
            ) : statusFilter ? (
              <EmptyState
                icon={<Building2 className="h-6 w-6" />}
                title={`No ${catalog.plural.toLowerCase()} match this filter`}
                description="Try a different status, or clear the filter to see everything."
                action={{ label: "Clear filter", onClick: () => setStatusFilter(null) }}
              />
            ) : (
              <EmptyState
                icon={<Building2 className="h-6 w-6" />}
                title={`No ${catalog.plural.toLowerCase()} yet`}
                description={`Add your first ${catalog.singular.toLowerCase()} so the assistant can match it to leads.`}
                action={{
                  label: `New ${catalog.singular.toLowerCase()}`,
                  onClick: () => setEditingId("new"),
                  icon: <Plus className="h-4 w-4" />,
                }}
              />
            )}
          </Card>
        )}
      </div>

      {editingId && (
        <PropertyEditDrawer
          propertyId={editingId === "new" ? null : editingId}
          onClose={() => setEditingId(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ["properties"] });
            setEditingId(null);
          }}
        />
      )}
    </div>
  );
}

function PropertyStatusBadge({ status }: { status: PropertyStatus }) {
  const map: Record<PropertyStatus, "success" | "warning" | "danger" | "muted" | "info"> = {
    available: "success",
    pending: "warning",
    rented: "danger",
    unavailable: "danger",
    archived: "muted",
  };
  return <Badge variant={map[status]}>{status}</Badge>;
}

// ---------------------------------------------------------------------------
// Edit drawer
// ---------------------------------------------------------------------------

interface FullProperty {
  id: string;
  name: string;
  area: string;
  type: PropertyType;
  bedrooms: number | null;
  bathrooms: number | null;
  monthly_price_idr: number | null;
  yearly_price_idr: number | null;
  deposit_idr: number | null;
  status: PropertyStatus;
  available_from: string | null;
  min_stay_months: number | null;
  short_description: string | null;
  description: string | null;
  lifestyle_tags: string[] | null;
  google_maps_url: string | null;
  address: string | null;
  owner_contact: string | null;
  onsite_contact_name: string | null;
  onsite_contact_phone: string | null;
  internal_notes: string | null;
  // House rules + amenities
  pets_policy: string | null;
  smoking_policy: string | null;
  parties_allowed: number | boolean | null;
  internet_status: string | null;
  internet_speed_mbps: number | null;
  utilities_policy: string | null;
  cleaning_policy: string | null;
  pool: number | boolean | null;
  aircon_policy: string | null;
  parking_spots: number | null;
  furnished_level: string | null;
  kitchen_level: string | null;
  max_guests: number | null;
  payment_terms_override: string | null;
  check_in_time: string | null;
  check_out_time: string | null;
  house_rules_text: string | null;
  is_active: number;
}

// Allowed values mirror the Zod enums on the worker.
const PETS_POLICIES   = ["no", "cats_only", "dogs_ok", "case_by_case", "yes"] as const;
const SMOKING_POLICIES = ["no", "outdoor_only", "yes"] as const;
const INTERNET_STATUSES = ["none", "basic", "fast", "fiber"] as const;
const UTILITIES_POLICIES = ["included", "capped", "metered"] as const;
const CLEANING_POLICIES = ["none", "weekly", "twice_weekly", "daily", "on_demand"] as const;
const AIRCON_POLICIES = ["none", "bedrooms_only", "all_rooms"] as const;
const FURNISHED_LEVELS = ["unfurnished", "semi", "full"] as const;
const KITCHEN_LEVELS = ["none", "kitchenette", "full"] as const;

interface MediaRow {
  id: string;
  url: string;
  media_type: string;
  alt_text: string | null;
  ordering: number;
}

function PropertyEditDrawer({
  propertyId,
  onClose,
  onSaved,
}: {
  propertyId: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = propertyId === null;
  const qc = useQueryClient();
  const toast = useToast();
  const [form, setForm] = useState<Partial<FullProperty>>(isNew ? defaultNew() : {});
  const [newMediaUrl, setNewMediaUrl] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["property", propertyId],
    queryFn: () => api.get<{ property: FullProperty; media: MediaRow[] }>(`/api/properties/${propertyId}`),
    enabled: !isNew,
  });

  const property = isNew ? form : { ...data?.property, ...form };
  const media = data?.media ?? [];

  const saveMut = useMutation({
    mutationFn: () => {
      const payload = stripUnchanged(form);
      if (isNew) return api.post<{ id: string }>("/api/properties", payload);
      return api.put<{ id: string }>(`/api/properties/${propertyId}`, payload);
    },
    onSuccess: () => {
      // Invalidate both the list and the single-property cache so the
      // drawer doesn't show stale fields when re-opened immediately.
      qc.invalidateQueries({ queryKey: ["property", propertyId] });
      toast.success(isNew ? "Created" : "Saved");
      onSaved();
    },
    onError: (e) =>
      toast.error("Could not save", e instanceof Error ? e.message : undefined),
  });

  const archiveMut = useMutation({
    mutationFn: () => api.del(`/api/properties/${propertyId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["property", propertyId] });
      toast.success("Archived");
      onSaved();
    },
    onError: (e) =>
      toast.error("Could not archive", e instanceof Error ? e.message : undefined),
  });

  const addMediaMut = useMutation({
    mutationFn: () => api.post(`/api/properties/${propertyId}/media`, { url: newMediaUrl, media_type: "image" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["property", propertyId] });
      setNewMediaUrl("");
    },
  });

  const deleteMediaMut = useMutation({
    mutationFn: (mediaId: string) => api.del(`/api/properties/${propertyId}/media/${mediaId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["property", propertyId] }),
  });

  function setField<K extends keyof FullProperty>(k: K, v: FullProperty[K] | null) {
    setForm({ ...form, [k]: v });
  }

  const p = property as Partial<FullProperty>;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/40">
      <div className="w-full max-w-2xl bg-brand-surface shadow-xl overflow-y-auto">
        <header className="sticky top-0 bg-brand-surface flex items-center justify-between border-b border-slate-200 px-5 py-3 z-10">
          <h2 className="font-semibold">{isNew ? "New property" : `Edit · ${p.name ?? ""}`}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X className="h-5 w-5" />
          </button>
        </header>

        {isLoading && !isNew ? (
          <div className="p-12 text-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400 mx-auto" /></div>
        ) : (
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Name *">
                <Input value={String(p.name ?? "")} onChange={(e) => setField("name", e.target.value)} />
              </Field>
              <Field label="Area *">
                <Input value={String(p.area ?? "")} onChange={(e) => setField("area", e.target.value)} />
              </Field>
              <Field label="Type *">
                <select className="h-9 w-full rounded-md border border-slate-300 px-2 text-sm"
                  value={String(p.type ?? "villa")}
                  onChange={(e) => setField("type", e.target.value as PropertyType)}>
                  {PROPERTY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Status">
                <select className="h-9 w-full rounded-md border border-slate-300 px-2 text-sm"
                  value={String(p.status ?? "available")}
                  onChange={(e) => setField("status", e.target.value as PropertyStatus)}>
                  {PROPERTY_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Bedrooms">
                <Input type="number" value={String(p.bedrooms ?? "")} onChange={(e) => setField("bedrooms", e.target.value === "" ? null : Number(e.target.value))} />
              </Field>
              <Field label="Bathrooms">
                <Input type="number" value={String(p.bathrooms ?? "")} onChange={(e) => setField("bathrooms", e.target.value === "" ? null : Number(e.target.value))} />
              </Field>
              <Field label="Monthly price (IDR)">
                <Input type="number" value={String(p.monthly_price_idr ?? "")} onChange={(e) => setField("monthly_price_idr", e.target.value === "" ? null : Number(e.target.value))} />
              </Field>
              <Field label="Deposit (IDR)">
                <Input type="number" value={String(p.deposit_idr ?? "")} onChange={(e) => setField("deposit_idr", e.target.value === "" ? null : Number(e.target.value))} />
              </Field>
              <Field label="Available from">
                <Input type="date" value={String(p.available_from ?? "")} onChange={(e) => setField("available_from", e.target.value || null)} />
              </Field>
              <Field label="Min stay (months)">
                <Input type="number" value={String(p.min_stay_months ?? "")} onChange={(e) => setField("min_stay_months", e.target.value === "" ? null : Number(e.target.value))} />
              </Field>
            </div>
            <Field label="Short description (≤ 280)">
              <Input maxLength={280} value={String(p.short_description ?? "")} onChange={(e) => setField("short_description", e.target.value)} />
            </Field>
            <Field label="Full description">
              <textarea rows={4} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                value={String(p.description ?? "")} onChange={(e) => setField("description", e.target.value)} />
            </Field>
            <Field label="Lifestyle tags (comma-separated)">
              <Input value={(p.lifestyle_tags ?? []).join(", ")}
                onChange={(e) => setField("lifestyle_tags", e.target.value.split(",").map((s) => s.trim()).filter(Boolean) as any)} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Address"><Input value={String(p.address ?? "")} onChange={(e) => setField("address", e.target.value)} /></Field>
              <Field label="Google Maps URL"><Input value={String(p.google_maps_url ?? "")} onChange={(e) => setField("google_maps_url", e.target.value)} /></Field>
              <Field label="Owner contact"><Input value={String(p.owner_contact ?? "")} onChange={(e) => setField("owner_contact", e.target.value)} /></Field>
              <Field label="Onsite contact name"><Input value={String(p.onsite_contact_name ?? "")} onChange={(e) => setField("onsite_contact_name", e.target.value)} /></Field>
              <Field label="Onsite contact phone"><Input value={String(p.onsite_contact_phone ?? "")} onChange={(e) => setField("onsite_contact_phone", e.target.value)} /></Field>
            </div>
            <Field label="Internal notes (never shown to prospects)">
              <textarea rows={3} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                value={String(p.internal_notes ?? "")} onChange={(e) => setField("internal_notes", e.target.value)} />
            </Field>

            {/* ----- House rules ----- */}
            <section className="border-t border-slate-200 pt-4 space-y-3">
              <h3 className="text-sm font-semibold text-slate-900">House rules</h3>
              <p className="text-[11px] text-slate-500 -mt-2">
                These get injected into the AI's context whenever this property is mentioned. The AI uses them as the
                ground truth - leave a field blank if you'd rather the AI escalate to a human.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Pets">
                  <SelectInput value={p.pets_policy ?? ""} onChange={(v) => setField("pets_policy", v || null)}
                    options={[{ value: "", label: "- not specified -" }, ...PETS_POLICIES.map((v) => ({ value: v, label: v.replace(/_/g, " ") }))]} />
                </Field>
                <Field label="Smoking">
                  <SelectInput value={p.smoking_policy ?? ""} onChange={(v) => setField("smoking_policy", v || null)}
                    options={[{ value: "", label: "- not specified -" }, ...SMOKING_POLICIES.map((v) => ({ value: v, label: v.replace(/_/g, " ") }))]} />
                </Field>
                <Field label="Parties allowed">
                  <SelectInput value={p.parties_allowed === null || p.parties_allowed === undefined ? "" : (p.parties_allowed ? "1" : "0")}
                    onChange={(v) => setField("parties_allowed", v === "" ? null : v === "1")}
                    options={[{ value: "", label: "- not specified -" }, { value: "1", label: "Yes" }, { value: "0", label: "No" }]} />
                </Field>
                <Field label="Max guests">
                  <Input type="number" min={0} max={50}
                    value={String(p.max_guests ?? "")}
                    onChange={(e) => setField("max_guests", e.target.value === "" ? null : Number(e.target.value))} />
                </Field>
                <Field label="Check-in time">
                  <Input type="time" value={String(p.check_in_time ?? "")} onChange={(e) => setField("check_in_time", e.target.value || null)} />
                </Field>
                <Field label="Check-out time">
                  <Input type="time" value={String(p.check_out_time ?? "")} onChange={(e) => setField("check_out_time", e.target.value || null)} />
                </Field>
              </div>
              <Field label="Payment terms override (use only if different from your business default)">
                <textarea rows={2} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                  placeholder="ex: This villa requires 6 months upfront instead of 3."
                  value={String(p.payment_terms_override ?? "")}
                  onChange={(e) => setField("payment_terms_override", e.target.value || null)} />
              </Field>
              <Field label="Free-form house rules (anything else worth saying)">
                <textarea rows={3} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                  placeholder="ex: Quiet hours from 22:00. No outside guests overnight without owner approval."
                  value={String(p.house_rules_text ?? "")}
                  onChange={(e) => setField("house_rules_text", e.target.value || null)} />
              </Field>
            </section>

            {/* ----- Amenities ----- */}
            <section className="border-t border-slate-200 pt-4 space-y-3">
              <h3 className="text-sm font-semibold text-slate-900">Amenities</h3>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Pool">
                  <SelectInput value={p.pool === null || p.pool === undefined ? "" : (p.pool ? "1" : "0")}
                    onChange={(v) => setField("pool", v === "" ? null : v === "1")}
                    options={[{ value: "", label: "- not specified -" }, { value: "1", label: "Yes" }, { value: "0", label: "No" }]} />
                </Field>
                <Field label="Aircon">
                  <SelectInput value={p.aircon_policy ?? ""} onChange={(v) => setField("aircon_policy", v || null)}
                    options={[{ value: "", label: "- not specified -" }, ...AIRCON_POLICIES.map((v) => ({ value: v, label: v.replace(/_/g, " ") }))]} />
                </Field>
                <Field label="Parking spots">
                  <Input type="number" min={0} max={20}
                    value={String(p.parking_spots ?? "")}
                    onChange={(e) => setField("parking_spots", e.target.value === "" ? null : Number(e.target.value))} />
                </Field>
                <Field label="Furnished">
                  <SelectInput value={p.furnished_level ?? ""} onChange={(v) => setField("furnished_level", v || null)}
                    options={[{ value: "", label: "- not specified -" }, ...FURNISHED_LEVELS.map((v) => ({ value: v, label: v }))]} />
                </Field>
                <Field label="Kitchen">
                  <SelectInput value={p.kitchen_level ?? ""} onChange={(v) => setField("kitchen_level", v || null)}
                    options={[{ value: "", label: "- not specified -" }, ...KITCHEN_LEVELS.map((v) => ({ value: v, label: v }))]} />
                </Field>
                <Field label="Internet">
                  <SelectInput value={p.internet_status ?? ""} onChange={(v) => setField("internet_status", v || null)}
                    options={[{ value: "", label: "- not specified -" }, ...INTERNET_STATUSES.map((v) => ({ value: v, label: v }))]} />
                </Field>
                <Field label="Internet speed (Mbps)">
                  <Input type="number" min={0} max={10000}
                    value={String(p.internet_speed_mbps ?? "")}
                    onChange={(e) => setField("internet_speed_mbps", e.target.value === "" ? null : Number(e.target.value))} />
                </Field>
                <Field label="Utilities">
                  <SelectInput value={p.utilities_policy ?? ""} onChange={(v) => setField("utilities_policy", v || null)}
                    options={[{ value: "", label: "- not specified -" }, ...UTILITIES_POLICIES.map((v) => ({ value: v, label: v }))]} />
                </Field>
                <Field label="Cleaning service">
                  <SelectInput value={p.cleaning_policy ?? ""} onChange={(v) => setField("cleaning_policy", v || null)}
                    options={[{ value: "", label: "- not specified -" }, ...CLEANING_POLICIES.map((v) => ({ value: v, label: v.replace(/_/g, " ") }))]} />
                </Field>
              </div>
            </section>

            {!isNew && (
              <div className="border-t border-slate-200 pt-4">
                <h3 className="text-sm font-semibold mb-2">Media URLs</h3>
                <div className="space-y-2">
                  {media.map((m) => (
                    <div key={m.id} className="flex items-center gap-2 text-xs">
                      <a href={m.url} target="_blank" rel="noopener noreferrer" className="flex-1 truncate text-blue-600 hover:underline">{m.url}</a>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          if (confirm("Remove this media item?")) deleteMediaMut.mutate(m.id);
                        }}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                  <div className="flex gap-2 mt-2">
                    <Input placeholder="https://..." value={newMediaUrl} onChange={(e) => setNewMediaUrl(e.target.value)} />
                    <Button size="sm" disabled={!newMediaUrl || addMediaMut.isPending} onClick={() => addMediaMut.mutate()}>Add</Button>
                  </div>
                </div>
              </div>
            )}

            <div className="border-t border-slate-200 pt-3 flex justify-between">
              {!isNew ? (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => {
                    if (
                      confirm(
                        `Archive ${property.name ? `"${property.name}"` : "this item"}? It will stop being matched to leads. You can re-activate it later.`,
                      )
                    )
                      archiveMut.mutate();
                  }}
                  disabled={archiveMut.isPending}
                >
                  <Archive className="h-4 w-4" />
                  Archive
                </Button>
              ) : <span />}
              <div className="flex gap-2">
                <Button variant="ghost" onClick={onClose}>Cancel</Button>
                <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
                  {saveMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Save
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function defaultNew(): Partial<FullProperty> {
  return {
    name: "",
    area: "",
    type: "villa",
    status: "available",
  };
}

function stripUnchanged(form: Partial<FullProperty>): Partial<FullProperty> {
  // Remove undefined keys
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(form)) {
    if (v !== undefined) out[k] = v;
  }
  return out as Partial<FullProperty>;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs font-medium text-slate-700">{label}</span>
      {children}
    </label>
  );
}

function SelectInput({
  value, onChange, options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full h-9 rounded-md border border-slate-300 px-2 text-sm bg-brand-surface"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}
